class GiftcardForm extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.updateSubmitVisibility()
    this.syncFromDom()
  }

  setupListeners () {
    this.handleSubmit = this.handleSubmit.bind(this)
    this.handleInput = this.handleInput.bind(this)
    this.onCartChanged = this.onCartChanged.bind(this)

    if (this?.els?.form?.exists) {
      this.els.form.elements.forEach(formEl => {
        this.listeners.add(formEl, 'submit', this.handleSubmit)
      })
    }

    if (this?.els?.input?.exists) {
      this.els.input.elements.forEach(inputEl => {
        this.listeners.add(inputEl, 'input', this.handleInput)
      })
    }

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.CHANGED,
      this.onCartChanged,
     )
  }

  disconnectedCallback () {
    window.BAO.eventBus.removeEventListener(
      window.BAO.EVENTS.PUSH_CART.CHANGED,
      this.onCartChanged,
    )

    super.disconnectedCallback()
  }

  onCartChanged (event) {
    const state = event.detail?.state

    if (state) {
      this.syncCurrentCodes(state)
      return
    }

    this.syncFromDom()
  }

  syncFromDom () {
    if (!this?.els?.input?.exists) return

    const codes = this.getAppliedCodesFromDom()

    if (codes.length) {
      this.els.input.element.dataset.current = codes.join(',')
    } else {
      delete this.els.input.element.dataset.current
    }
  }

  handleInput () {
    this.clearError()
    this.resetSubmitLabel()
    this.updateSubmitVisibility()
  }

  resetSubmitLabel () {
    if (!this?.els?.submit?.exists) return

    this.els.submit.element.value = this.els.submit.element.dataset.apply
  }

  updateSubmitVisibility () {
    if (!this?.els?.submit?.exists || !this?.els?.input?.exists) return

    const hasValue = this.els.input.element.value.trim().length > 0

    this.els.submit.element.setAttribute('aria-hidden', hasValue ? 'false' : 'true')
  }

  clearError () {
    this.removeAttribute('aria-invalid')

    if (!this?.els?.error?.exists) return

    this.els.error.element.textContent =
      this.els.error.element.dataset.errorInvalid || ''
  }

  getAppliedCodesFromDom () {
    const section =
      this.closest('[data-section-type="push-cart-footer"]') ||
      this.closest('.shopify-section')

    if (!section) return []

    return Array.from(section.querySelectorAll('[data-discount-code]'))
      .map(element => element.dataset.discountCode?.trim())
      .filter(Boolean)
  }

  getAllAppliedCodes () {
    return this.getAppliedCodesFromDom()
  }

  getExistingCodes () {
    this.syncFromDom()

    return this.getAppliedCodesFromDom()
  }

  isCodeAlreadyOnCart (code) {
    const normalised = code.trim().toLowerCase()

    return this.getAllAppliedCodes().some(
      existingCode => existingCode.toLowerCase() === normalised,
    )
  }

  buildDiscountCodes (existingCodes, newCode) {
    const seen = new Set()
    const codes = []

    for (const code of [...existingCodes, newCode]) {
      const key = code.toLowerCase()

      if (seen.has(key)) continue

      seen.add(key)
      codes.push(code)
    }

    return codes.join(',')
  }

  async handleSubmit (event) {
    event.preventDefault()

    const formData = new FormData(event.target)
    const newCode = (formData.get('discount') || '').trim()

    if (!newCode) return

    this.syncFromDom()
    this._appliedCodesBeforeSubmit = this.getAppliedCodesFromDom()
    const existingCodes = this.getExistingCodes()

    if (this.isCodeAlreadyOnCart(newCode)) {
      this.els.input.element.value = ''
      this.updateSubmitVisibility()
      this.showError('applied')
      return
    }

    this._pendingExistingCodes = existingCodes.join(',')
    this._pendingSubmittedCode = newCode

    const discountCodes = this.buildDiscountCodes(existingCodes, newCode)

    await window.BAO.Cart.updateCartDiscount(discountCodes)
  }

  wasSubmittedCodeApplied (state, submittedCode) {
    if (!submittedCode || !state) return false

    const normalised = submittedCode.trim().toLowerCase()
    const matchedCode = state.discount_codes?.find(
      discountCode =>
        discountCode.code?.trim().toLowerCase() === normalised,
    )

    if (matchedCode?.applicable === true) return true

    return state.cart_level_discount_applications?.some(
      application => application.title?.trim().toLowerCase() === normalised,
    )
  }

  isCombinationFailure (state, submittedCode, existingCodesSent) {
    if (!submittedCode || !state) return false
    if (this.wasSubmittedCodeApplied(state, submittedCode)) return false

    const normalised = submittedCode.trim().toLowerCase()
    const matchedCode = state.discount_codes?.find(
      discountCode =>
        discountCode.code?.trim().toLowerCase() === normalised,
    )

    if (matchedCode?.applicable === true) return false

    const existingCodes = (existingCodesSent || '')
      .split(',')
      .map(code => code.trim())
      .filter(Boolean)

    if (existingCodes.length > 0) return true

    const otherCodeApplicable = state.discount_codes?.some(
      discountCode =>
        discountCode.code?.trim().toLowerCase() !== normalised &&
        discountCode.applicable === true,
    )

    if (otherCodeApplicable) return true

    return state.cart_level_discount_applications?.some(
      application => application.title?.trim().toLowerCase() !== normalised,
    )
  }

  showError (type) {
    if (!this?.els?.error?.exists) return

    const errorEl = this.els.error.element
    const messages = {
      applied: errorEl.dataset.errorApplied,
      combine: errorEl.dataset.errorCombine,
      invalid: errorEl.dataset.errorInvalid,
    }
    const message = messages[type]

    if (message) errorEl.textContent = message

    this.setAttribute('aria-invalid', 'true')
    this.resetSubmitLabel()
  }

  handleResponse (state, submittedCode) {
    if (!submittedCode) return

    if (this.wasSubmittedCodeApplied(state, submittedCode)) {
      const normalised = submittedCode.trim().toLowerCase()
      const wasDuplicate = (this._appliedCodesBeforeSubmit || []).some(
        code => code.toLowerCase() === normalised,
      )

      this.els.input.element.value = ''
      this.resetSubmitLabel()
      this.updateSubmitVisibility()
      this.syncCurrentCodes(state)

      if (wasDuplicate) {
        this.showError('applied')
      } else {
        this.clearError()
      }

      return
    }

    const failureType = this.isCombinationFailure(
      state,
      submittedCode,
      this._pendingExistingCodes || '',
    )
      ? 'combine'
      : 'invalid'

    this.showError(failureType)
  }

  syncCurrentCodes (state) {
    if (!state) return

    const codesFromApi =
      state.discount_codes
        ?.filter(discountCode => discountCode.applicable)
        .map(discountCode => discountCode.code) || []

    const codesFromCart =
      state.cart_level_discount_applications
        ?.filter(application => application.type === 'discount_code')
        .map(application => application.title) || []

    const applicableCodes = [...new Set([...codesFromApi, ...codesFromCart])]
      .filter(Boolean)
      .join(',')

    if (applicableCodes) {
      this.els.input.element.dataset.current = applicableCodes
    } else {
      delete this.els.input.element.dataset.current
    }
  }
}

if (!customElements.get('giftcard-form')) {
  customElements.define('giftcard-form', GiftcardForm)
}
